/**
 * extractDeleteOutline — token-aware DELETE statement walker. Returns:
 *  - the optional pre-FROM target list (MySQL `DELETE t1, t2 FROM …`),
 *  - the primary target table (post-FROM, with optional alias),
 *  - the optional multi-table tail (JOINs / commas) that follow the target,
 *  - the optional `USING …` clause body (PG / MySQL form B),
 *  - the WHERE predicate body, and
 *  - the optional RETURNING expression list (PG / Oracle / SQLite ≥ 3.35).
 *
 * Pure / structural — uses `tokenSkip` so dialect-specific quoting (backticks,
 * brackets, dollar-tags, hash comments) is invisible to brace counting
 * (VQE-RO9 — no regex parsing). Reuses the shared low-level token primitives
 * in `../insert/insertParseHelpers`.
 */

import type { ParseOptions, SqlRange } from '../../../../sql-grammar/model';
import { skipContextFor } from '../../../../sql-resolver/classify/tokenSkip';
import {
  readIdentifier,
  readWord,
  skipWsAndComments,
} from '../insert/insertParseHelpers';
import { findTopLevelKeyword, spanHasJoinOrComma, trimSpan, type KeywordHit } from '../common/statementScan';

export interface DeleteOutline {
  /** True only when the leading word in the statement range is `DELETE`. */
  readonly hasDelete: boolean;
  /**
   * Pre-FROM target list (MySQL `DELETE t1, t2 FROM …` form). The text spans
   * from the position after `DELETE` up to the next `FROM`. Null for the
   * standard form (where `FROM` immediately follows `DELETE`).
   */
  readonly preFromTargetText: string | null;
  readonly preFromTargetStart: number | null;
  readonly preFromTargetEnd: number | null;
  /**
   * Primary target table text exactly as written, including an optional alias.
   * Null only when the walker fails to read an identifier after FROM.
   */
  readonly targetText: string | null;
  readonly targetStart: number | null;
  readonly targetEnd: number | null;
  /**
   * Span between the target's end and the next clause keyword
   * (`USING` / `WHERE` / `RETURNING`), whitespace-trimmed. Non-empty when the
   * DELETE has a multi-table tail directly after the FROM target — JOIN list
   * (MySQL form A) or comma list. Null when the target is immediately followed
   * by USING / WHERE / RETURNING / end-of-statement.
   */
  readonly multiTableStart: number | null;
  readonly multiTableEnd: number | null;
  /** PG / MySQL `USING …` clause body span (excluding the keyword). */
  readonly usingStart: number | null;
  readonly usingEnd: number | null;
  /** WHERE predicate span (excluding the keyword). Ends BEFORE a trailing
   *  MySQL single-table `ORDER BY` / `LIMIT` (I7). */
  readonly whereStart: number | null;
  readonly whereEnd: number | null;
  /** Trailing `ORDER BY …` span (MySQL single-table DELETE). Non-null = present. */
  readonly orderByStart: number | null;
  readonly orderByEnd: number | null;
  /** Trailing `LIMIT …` span (MySQL single-table DELETE). Non-null = present. */
  readonly limitStart: number | null;
  readonly limitEnd: number | null;
  /** RETURNING expression list span (excluding the keyword). */
  readonly returningStart: number | null;
  readonly returningEnd: number | null;
  /**
   * True only when the statement joins a relation OTHER than the target — a
   * MySQL form-A `… FROM t JOIN …` tail, or a PG/MySQL `USING …` clause with a
   * JOIN or a second relation. A plain single-table DELETE (incl. the MSSQL
   * alias-target self-FROM `DELETE p FROM the-target p`) is NOT a join tail.
   * Drives the empty-WHERE "every row" scope warning (I8).
   */
  readonly hasJoinTail: boolean;
}

const EMPTY_OUTLINE: DeleteOutline = {
  hasDelete: false,
  preFromTargetText: null, preFromTargetStart: null, preFromTargetEnd: null,
  targetText: null, targetStart: null, targetEnd: null,
  multiTableStart: null, multiTableEnd: null,
  usingStart: null, usingEnd: null,
  whereStart: null, whereEnd: null,
  orderByStart: null, orderByEnd: null,
  limitStart: null, limitEnd: null,
  returningStart: null, returningEnd: null,
  hasJoinTail: false,
};

/**
 * Words that block a bare (non-`AS`) alias read after the DELETE target.
 * `ORDER` / `LIMIT` are only real trailing-clause keywords on MySQL
 * single-table DELETE (I7) — on MSSQL/Oracle/PG/SQLite `LIMIT` is a legal
 * unquoted identifier, so those engines must use the base set to stay
 * byte-identical to the pre-I7 parse (`DELETE FROM t limit WHERE …` reads
 * `limit` as the alias there, same as before I7).
 */
const BASE_ALIAS_BLOCK_KEYWORDS: ReadonlySet<string> = new Set([
  'USING', 'WHERE', 'RETURNING',
  'INNER', 'LEFT', 'RIGHT', 'FULL', 'CROSS', 'JOIN', 'ON',
  'AS',
]);
const MYSQL_ALIAS_BLOCK_KEYWORDS: ReadonlySet<string> = new Set([
  ...BASE_ALIAS_BLOCK_KEYWORDS, 'ORDER', 'LIMIT',
]);

export function extractDeleteOutline(
  source: string,
  statementRange: SqlRange,
  options: ParseOptions,
): DeleteOutline {
  const start = Math.max(0, statementRange.startOffset);
  const end = Math.max(start, Math.min(source.length, statementRange.endOffset));
  const ctx = skipContextFor(options);

  let i = skipWsAndComments(source, start, end);
  const deleteWord = readWord(source, i, end);
  if (!deleteWord || deleteWord.upper !== 'DELETE') return EMPTY_OUTLINE;
  i = skipWsAndComments(source, deleteWord.end, end);

  let preFromTargetText: string | null = null;
  let preFromTargetStart: number | null = null;
  let preFromTargetEnd: number | null = null;

  const maybeFrom = readWord(source, i, end);
  if (maybeFrom && maybeFrom.upper === 'FROM') {
    i = skipWsAndComments(source, maybeFrom.end, end);
  } else {
    const fromHit = findTopLevelKeyword(source, i, end, ctx, ['FROM']);
    if (!fromHit) return { ...EMPTY_OUTLINE, hasDelete: true };
    const trimmed = trimSpan(source, i, fromHit.start);
    if (trimmed) {
      preFromTargetText = source.slice(trimmed.start, trimmed.end);
      preFromTargetStart = trimmed.start;
      preFromTargetEnd = trimmed.end;
    }
    i = skipWsAndComments(source, fromHit.end, end);
  }

  const isMysql = options.engine === 'mysql';
  const target = readQualifiedTargetWithAlias(
    source, i, end,
    isMysql ? MYSQL_ALIAS_BLOCK_KEYWORDS : BASE_ALIAS_BLOCK_KEYWORDS,
  );
  if (!target) {
    return {
      ...EMPTY_OUTLINE,
      hasDelete: true,
      preFromTargetText, preFromTargetStart, preFromTargetEnd,
    };
  }

  // ORDER / LIMIT are trailing clauses in MySQL single-table DELETE ONLY
  // (I7) — see BASE_ALIAS_BLOCK_KEYWORDS doc comment for why MSSQL/Oracle/PG/
  // SQLite must not treat them as tail keywords (`LIMIT` is a legal unquoted
  // identifier there).
  const nextKw = findTopLevelKeyword(
    source, target.end, end, ctx,
    isMysql ? ['USING', 'WHERE', 'ORDER', 'LIMIT', 'RETURNING'] : ['USING', 'WHERE', 'RETURNING'],
  );
  const tailStartCursor = nextKw ? nextKw.start : end;
  const multiTable = trimSpan(source, target.end, tailStartCursor);

  let usingStart: number | null = null;
  let usingEnd: number | null = null;
  let whereStart: number | null = null;
  let whereEnd: number | null = null;
  let orderByStart: number | null = null;
  let orderByEnd: number | null = null;
  let limitStart: number | null = null;
  let limitEnd: number | null = null;
  let returningStart: number | null = null;
  let returningEnd: number | null = null;

  let cursor = nextKw ? nextKw.end : end;
  let kw: KeywordHit | null = nextKw;
  while (kw) {
    if (kw.upper === 'USING') {
      const next = findTopLevelKeyword(
        source, cursor, end, ctx,
        isMysql ? ['WHERE', 'ORDER', 'LIMIT', 'RETURNING'] : ['WHERE', 'RETURNING'],
      );
      usingStart = cursor;
      usingEnd = next ? next.start : end;
      cursor = next ? next.end : end;
      kw = next;
      continue;
    }
    if (kw.upper === 'WHERE') {
      const next = findTopLevelKeyword(
        source, cursor, end, ctx,
        isMysql ? ['ORDER', 'LIMIT', 'RETURNING'] : ['RETURNING'],
      );
      whereStart = cursor;
      whereEnd = next ? next.start : end;
      cursor = next ? next.end : end;
      kw = next;
      continue;
    }
    if (kw.upper === 'ORDER') {
      const next = findTopLevelKeyword(source, cursor, end, ctx, ['LIMIT', 'RETURNING']);
      orderByStart = cursor;
      orderByEnd = next ? next.start : end;
      cursor = next ? next.end : end;
      kw = next;
      continue;
    }
    if (kw.upper === 'LIMIT') {
      const next = findTopLevelKeyword(source, cursor, end, ctx, ['RETURNING']);
      limitStart = cursor;
      limitEnd = next ? next.start : end;
      cursor = next ? next.end : end;
      kw = next;
      continue;
    }
    if (kw.upper === 'RETURNING') {
      returningStart = cursor;
      returningEnd = end;
      break;
    }
    break;
  }

  const hasJoinTail =
    (usingStart !== null && usingEnd !== null && spanHasJoinOrComma(source, usingStart, usingEnd, ctx)) ||
    (multiTable !== null && spanHasJoinOrComma(source, multiTable.start, multiTable.end, ctx));

  return {
    hasDelete: true,
    preFromTargetText, preFromTargetStart, preFromTargetEnd,
    targetText: target.text,
    targetStart: target.start,
    targetEnd: target.end,
    multiTableStart: multiTable ? multiTable.start : null,
    multiTableEnd: multiTable ? multiTable.end : null,
    usingStart, usingEnd,
    whereStart, whereEnd,
    orderByStart, orderByEnd,
    limitStart, limitEnd,
    returningStart, returningEnd,
    hasJoinTail,
  };
}

interface TargetMatch {
  readonly text: string;
  readonly start: number;
  readonly end: number;
}

function readQualifiedTargetWithAlias(
  source: string,
  from: number,
  end: number,
  aliasBlockKeywords: ReadonlySet<string>,
): TargetMatch | null {
  const start = from;
  const first = readIdentifier(source, from, end);
  if (!first) return null;
  let i = first.end;
  while (i < end && source[i] === '.') {
    const next = readIdentifier(source, i + 1, end);
    if (!next) break;
    i = next.end;
  }
  const baseEnd = i;
  const afterBase = skipWsAndComments(source, baseEnd, end);
  const word = readWord(source, afterBase, end);
  if (word && word.upper === 'AS') {
    const afterAs = skipWsAndComments(source, word.end, end);
    const aliasIdent = readIdentifier(source, afterAs, end);
    if (aliasIdent) {
      return { text: source.slice(start, aliasIdent.end), start, end: aliasIdent.end };
    }
  } else if (afterBase > baseEnd) {
    const aliasIdent = readIdentifier(source, afterBase, end);
    if (aliasIdent) {
      const aliasUpper = source.slice(aliasIdent.start, aliasIdent.end).toUpperCase();
      if (!aliasBlockKeywords.has(aliasUpper)) {
        return { text: source.slice(start, aliasIdent.end), start, end: aliasIdent.end };
      }
    }
  }
  return { text: source.slice(start, baseEnd), start, end: baseEnd };
}

